import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Power, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { MASTERS } from "@/lib/masters";
import { RefLabel, clearLookups } from "@/lib/lookups";
import { errMsg, fmtDate, fmtDateTime, fmtINR } from "@/lib/format";
import { ErrorState, LoadingRows, NoAccess, Badge } from "@/components/common/States";
import { getPath } from "./FieldInput";
import { EntityForm } from "./EntityForm";
import { FilesPanel } from "./FilesPanel";
import { EXTRAS } from "@/pages/masters/Extras";

const Value = ({ f, v }) => {
  const { branches } = useSession();
  const empty = <span className="text-muted-foreground">—</span>;
  if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) return f.type === "switch" ? "No" : empty;
  switch (f.type) {
    case "select": return f.options.find((o) => o.value === v)?.label || String(v);
    case "multiselect": return v.map((x) => f.options.find((o) => o.value === x)?.label || x).join(", ");
    case "switch": return v ? "Yes" : "No";
    case "date": return fmtDate(v);
    case "tags": return v.join(", ");
    case "branch": return branches.find((b) => b.id === v)?.name || "Outside your access";
    case "branches": return v.map((id) => branches.find((b) => b.id === id)?.code || "•").join(", ");
    case "ref": return <RefLabel kind={f.ref} id={v} />;
    case "refmulti": return <span className="flex flex-wrap gap-x-2">{v.map((id) => <RefLabel key={id} kind={f.ref} id={id} />)}</span>;
    case "contacts": return (
      <ul className="space-y-1">{v.map((c, i) => <li key={i}>{c.name}{c.designation ? ` (${c.designation})` : ""} · {c.phone || "—"} · {c.email || "—"} {c.is_authorized_rep && <Badge tone="gold">Authorized rep</Badge>}</li>)}</ul>
    );
    default: return /charge|price|cost|limit|rate \(/.test(f.label || "") && typeof v === "number" ? fmtINR(v) : String(v);
  }
};

const DetailsView = ({ cfg, record }) => (
  <div className="grid gap-5">
    {cfg.sections.map((s) => {
      if (s.show && !s.show(record)) return null;
      const restricted = s.restrictKey && record[s.restrictKey]?.restricted;
      return (
        <div key={s.title} className="g39-card p-5" data-testid={`detail-section-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <div className="font-display font-semibold mb-4 flex items-center gap-2">{s.title}{restricted && <Badge tone="gold"><Lock className="h-3 w-3" />Restricted</Badge>}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
            {s.fields.filter((f) => !f.show || f.show(record)).map((f) => {
              const v = getPath(record, f.name);
              return (
                <div key={f.name} className={f.span === 2 || f.type === "contacts" ? "sm:col-span-2 lg:col-span-3" : ""}>
                  {f.label && <div className="g39-label">{f.label}</div>}
                  <div className="text-sm mt-1 break-words" data-testid={`detail-${f.name.replace(/[._]/g, "-")}`}><Value f={f} v={v} /></div>
                </div>
              );
            })}
          </div>
        </div>
      );
    })}
  </div>
);

const Activity = ({ cfg, id }) => {
  const { data, loading, error, reload } = useFetch(`/${cfg.kind}/${id}/activity`);
  if (loading) return <LoadingRows rows={3} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div className="g39-card divide-y" data-testid="activity-panel">
      {data.map((e) => (
        <div key={e.id} className="px-4 py-3 text-sm">
          <div>{e.summary}</div>
          <div className="text-[11px] text-muted-foreground">{e.actor_name} · {fmtDateTime(e.at)} · <span className="font-mono-g">{e.action}</span></div>
          {Object.keys(e.changes || {}).length > 0 && <div className="text-[11px] font-mono-g text-gold mt-1 break-all">{Object.keys(e.changes).join(", ")}</div>}
        </div>
      ))}
    </div>
  );
};

const StatusDialog = ({ cfg, record, open, onOpenChange, onDone }) => {
  const { api } = useSession();
  const [reason, setReason] = useState("");
  const next = record.status === "active" ? "inactive" : "active";
  const go = async () => {
    try {
      await api.post(`/${cfg.kind}/${record.id}/status`, { status: next, reason: reason || null });
      clearLookups();
      toast.success(next === "inactive" ? `${cfg.label} deactivated` : `${cfg.label} reactivated`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="status-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{next === "inactive" ? "Deactivate" : "Reactivate"} {cfg.label.toLowerCase()}?</AlertDialogTitle>
          <AlertDialogDescription>{next === "inactive" ? "Inactive records stay in history but can't be selected for new work." : "The record becomes selectable again."}</AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea placeholder="Reason (recorded in the audit log)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="status-reason-input" />
        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={go} data-testid="status-confirm-button">Confirm</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default function EntityDetail({ kind }) {
  const cfg = MASTERS[kind];
  const { id } = useParams();
  const { can } = useSession();
  const { data: record, loading, error, reload } = useFetch(`/${kind}/${id}`, null, can(`${cfg.perm}.view`));
  const [edit, setEdit] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  if (!can(`${cfg.perm}.view`)) return <NoAccess perm={`${cfg.perm}.view`} />;
  if (loading && !record) return <div className="g39-card"><LoadingRows /></div>;
  if (error) return <div className="g39-card"><ErrorState message={error} onRetry={reload} /></div>;
  const Extra = EXTRAS[kind];

  return (
    <div data-testid={`${cfg.entity}-detail-page`}>
      <Link to={cfg.path} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-list"><ArrowLeft className="h-4 w-4" />{cfg.plural}</Link>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 g39-rise">
        <div>
          <div className="g39-label text-gold mb-1">{cfg.label}</div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="detail-title">{cfg.title(record)}</h1>
          <div className="flex items-center gap-2 mt-1.5 text-sm text-muted-foreground">
            <span className="font-mono-g">{cfg.code(record)}</span><Badge tone={record.status} data-testid="detail-status">{record.status}</Badge>
          </div>
        </div>
        {can(`${cfg.perm}.manage`) && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStatusOpen(true)} data-testid="toggle-status-button"><Power className="h-4 w-4 mr-1.5" />{record.status === "active" ? "Deactivate" : "Reactivate"}</Button>
            <Button onClick={() => setEdit(true)} data-testid="edit-record-button"><Pencil className="h-4 w-4 mr-1.5" />Edit</Button>
          </div>
        )}
      </div>
      <Tabs defaultValue={Extra ? "relations" : "details"}>
        <TabsList className="mb-4 flex-wrap h-auto">
          {Extra && <TabsTrigger value="relations" data-testid="tab-relations">{Extra.tab}</TabsTrigger>}
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>
        {Extra && <TabsContent value="relations"><Extra.Component cfg={cfg} record={record} reload={reload} /></TabsContent>}
        <TabsContent value="details"><DetailsView cfg={cfg} record={record} /></TabsContent>
        <TabsContent value="documents"><FilesPanel cfg={cfg} record={record} /></TabsContent>
        <TabsContent value="activity"><Activity cfg={cfg} id={record.id} /></TabsContent>
      </Tabs>
      <div className="text-[11px] text-muted-foreground mt-6" data-testid="record-metadata">
        Created by {record.created_by_name || "—"} on {fmtDateTime(record.created_at)} · Last modified by {record.updated_by_name || "—"} on {fmtDateTime(record.updated_at)} · Version {record.version}
      </div>
      <EntityForm cfg={cfg} open={edit} onOpenChange={setEdit} record={record} onSaved={reload} />
      <StatusDialog cfg={cfg} record={record} open={statusOpen} onOpenChange={setStatusOpen} onDone={() => { setStatusOpen(false); reload(); }} />
    </div>
  );
}
